import { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";

import { IconButton } from "@/components/ui/icon-button";
import { useDocumentViewport } from "@/features/document-viewport/context";
import { ensurePdfWorkerConfigured } from "@/features/document-viewport/pdf-worker";
import type { DocumentSource } from "@/features/document-viewport/types";
import { cn } from "@/lib/utils";

/** CSS width of a page thumbnail inside the sidebar list. */
const THUMBNAIL_WIDTH = 160;
/**
 * Extra left inset the host reserves while the sidebar is open so the document
 * shifts right instead of sitting underneath the panel.
 * (panel w-44 = 176px + left-3 gutter = 12px + 12px breathing room)
 */
export const PAGE_SIDEBAR_LEFT_INSET = 200;

interface DocumentViewportPageSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function isRenderingCancelled(error: unknown) {
  return (
    !!error &&
    typeof error === "object" &&
    "name" in error &&
    error.name === "RenderingCancelledException"
  );
}

/**
 * Loads a dedicated `PDFDocumentProxy` for thumbnail rendering. The main
 * viewport keeps its own proxy private, so the sidebar renders from a second,
 * independently destroyed document handle.
 *
 * Pass `enabled=false` while the sidebar is collapsed so we do not download or
 * allocate a second PDF until thumbnails are actually visible.
 */
function useSidebarPdfDocument(source: DocumentSource, enabled: boolean) {
  const [pdfDocument, setPdfDocument] = useState<pdfjs.PDFDocumentProxy | null>(null);
  const { fileUrl, revisionId, mediaKind } = source;

  useEffect(() => {
    if (!enabled || mediaKind !== "pdf") {
      setPdfDocument(null);
      return;
    }

    let cancelled = false;
    let loadingTask: pdfjs.PDFDocumentLoadingTask | null = null;
    let destruction: Promise<void> | null = null;

    const destroyLoadingTask = () => {
      if (loadingTask && !destruction) {
        destruction = loadingTask.destroy().catch(() => {});
      }
      return destruction;
    };

    const loadDocument = async () => {
      try {
        await ensurePdfWorkerConfigured();
        if (cancelled) {
          return;
        }

        loadingTask = pdfjs.getDocument({
          url: fileUrl,
          withCredentials: false,
        });
        const document = await loadingTask.promise;
        if (cancelled) {
          return;
        }

        setPdfDocument(document);
      } catch {
        void destroyLoadingTask();
        // The main viewport surfaces load errors; the sidebar stays empty.
        if (!cancelled) {
          setPdfDocument(null);
        }
      }
    };

    void loadDocument();

    return () => {
      cancelled = true;
      setPdfDocument(null);
      void destroyLoadingTask();
    };
  }, [enabled, fileUrl, revisionId, mediaKind]);

  return pdfDocument;
}

interface PageThumbnailProps {
  pdfDocument: pdfjs.PDFDocumentProxy | null;
  pageNumber: number;
  label?: string;
  isActive: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  onSelect: (pageNumber: number) => void;
  activeButtonRef: React.RefObject<HTMLButtonElement | null>;
}

function PageThumbnail({
  pdfDocument,
  pageNumber,
  label,
  isActive,
  scrollContainerRef,
  onSelect,
  activeButtonRef,
}: PageThumbnailProps) {
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const renderTaskRef = useRef<pdfjs.RenderTask | null>(null);
  const renderedRef = useRef(false);
  const inFlightRef = useRef(false);
  const renderGenerationRef = useRef(0);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas || !pdfDocument) {
      return;
    }

    let cancelled = false;

    const clearThumbnail = () => {
      // Invalidate any in-flight getPage/render so off-screen completions abort.
      renderGenerationRef.current += 1;
      inFlightRef.current = false;
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
      if (renderedRef.current) {
        renderedRef.current = false;
        setRendered(false);
      }
      // Releasing the bitmap keeps memory bounded for large documents.
      canvas.width = 0;
      canvas.height = 0;
    };

    const renderThumbnail = async () => {
      if (
        cancelled ||
        renderedRef.current ||
        renderTaskRef.current ||
        inFlightRef.current
      ) {
        return;
      }

      const generation = renderGenerationRef.current;
      inFlightRef.current = true;

      let page: pdfjs.PDFPageProxy | null = null;
      try {
        page = await pdfDocument.getPage(pageNumber);
        if (
          cancelled ||
          generation !== renderGenerationRef.current ||
          renderedRef.current
        ) {
          page.cleanup();
          return;
        }

        const baseViewport = page.getViewport({ scale: 1 });
        const pixelRatio =
          typeof window === "undefined"
            ? 1
            : Math.min(2, Math.max(1, window.devicePixelRatio || 1));
        const scale = (THUMBNAIL_WIDTH * pixelRatio) / baseViewport.width;
        const viewport = page.getViewport({ scale });

        canvas.width = Math.max(1, Math.floor(viewport.width));
        canvas.height = Math.max(1, Math.floor(viewport.height));

        const context = canvas.getContext("2d", { alpha: false });
        if (!context) {
          page.cleanup();
          return;
        }

        const renderTask = page.render({
          canvas,
          canvasContext: context,
          viewport,
          background: "rgb(255, 255, 255)",
        });
        renderTaskRef.current = renderTask;
        await renderTask.promise;
        if (renderTaskRef.current === renderTask) {
          renderTaskRef.current = null;
        }
        if (
          !cancelled &&
          generation === renderGenerationRef.current
        ) {
          renderedRef.current = true;
          setRendered(true);
        }
        page.cleanup();
      } catch (error: unknown) {
        renderTaskRef.current = null;
        page?.cleanup();
        if (!isRenderingCancelled(error) && !cancelled) {
          // Leave the placeholder visible; the main viewport surfaces errors.
        }
      } finally {
        if (generation === renderGenerationRef.current) {
          inFlightRef.current = false;
        }
      }
    };

    if (typeof IntersectionObserver === "undefined") {
      void renderThumbnail();
      return () => {
        cancelled = true;
        clearThumbnail();
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            void renderThumbnail();
          } else {
            clearThumbnail();
          }
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: "400px 0px",
      }
    );
    observer.observe(container);

    return () => {
      cancelled = true;
      observer.disconnect();
      clearThumbnail();
    };
  }, [pdfDocument, pageNumber, scrollContainerRef]);

  return (
    <button
      type="button"
      ref={isActive ? activeButtonRef : undefined}
      onClick={() => onSelect(pageNumber)}
      aria-label={label ? `Go to page ${pageNumber}, ${label}` : `Go to page ${pageNumber}`}
      aria-current={isActive ? "page" : undefined}
      className="group flex w-full flex-col items-center gap-1 rounded-md p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span
        ref={containerRef}
        className={cn(
          "relative block w-full overflow-hidden rounded-sm border-2 bg-white shadow-sm transition-colors",
          isActive
            ? "border-primary"
            : "border-border group-hover:border-primary/50"
        )}
      >
        {rendered ? null : (
          <span
            className="block aspect-[8.5/11] w-full animate-pulse bg-muted/50"
            aria-hidden="true"
          />
        )}
        <canvas
          ref={canvasRef}
          className={
            rendered
              ? "block h-auto w-full"
              : "pointer-events-none absolute inset-0 h-full w-full opacity-0"
          }
        />
      </span>
      <span
        className={cn(
          "w-full text-center text-xs font-medium tabular-nums",
          isActive ? "font-semibold text-primary" : "text-muted-foreground"
        )}
      >
        {pageNumber}
        {label ? <span className="line-clamp-2 break-words font-normal">{label}</span> : null}
      </span>
    </button>
  );
}

/**
 * Collapsible, scrollable page-thumbnail rail for PDF documents, modeled after
 * the pdf.js sidebar. Clicking a thumbnail navigates the viewport to that page.
 */
export function DocumentViewportPageSidebar({
  open,
  onOpenChange,
}: DocumentViewportPageSidebarProps) {
  const viewport = useDocumentViewport();
  const { source, pageCount, activePage, goToPage, pageLabels } = viewport;
  const pdfDocument = useSidebarPdfDocument(source, open);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const activeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    activeButtonRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [activePage, open, pageCount]);

  if (source.mediaKind !== "pdf") {
    return null;
  }

  if (!open) {
    return (
      <IconButton
        label="Show page thumbnails"
        icon={PanelLeftOpen}
        variant="outline"
        tooltipSide="right"
        data-viewport-tools-ui
        data-testid="page-sidebar-toggle"
        onClick={() => onOpenChange(true)}
        className="absolute left-3 top-16 z-30 bg-background/95 shadow-sm backdrop-blur"
      />
    );
  }

  const pages = Array.from({ length: pageCount }, (_, index) => index + 1);

  return (
    <aside
      data-viewport-tools-ui
      data-testid="page-sidebar"
      aria-label="Page thumbnails"
      className="absolute bottom-4 left-3 top-16 z-30 flex w-44 flex-col rounded-lg border-2 border-border bg-background/95 shadow-xl backdrop-blur"
    >
      <div className="flex items-center justify-between gap-2 border-b border-border/70 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Pages
        </span>
        <IconButton
          label="Hide page thumbnails"
          icon={PanelLeftClose}
          size="xs"
          iconClassName="size-4"
          tooltipSide="right"
          onClick={() => onOpenChange(false)}
        />
      </div>
      <div
        ref={scrollContainerRef}
        className="custom-scrollbar min-h-0 flex-1 overflow-y-auto p-2"
      >
        <div className="flex flex-col items-center gap-1">
          {pages.map((pageNumber) => (
            <PageThumbnail
              key={`${source.revisionId}-${pageNumber}`}
              pdfDocument={pdfDocument}
              pageNumber={pageNumber}
              label={pageLabels?.get(pageNumber)}
              isActive={pageNumber === activePage}
              scrollContainerRef={scrollContainerRef}
              onSelect={goToPage}
              activeButtonRef={activeButtonRef}
            />
          ))}
        </div>
      </div>
    </aside>
  );
}
